import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  TrendingUp, Calendar, Users, Car, Package, Download,
  Award, Target, Clock, Briefcase, MapPin, AlertTriangle,
  BarChart3, ArrowUp, ArrowDown, Minus
} from 'lucide-react';

// Monthly Analytics Component
export const MonthlyAnalytics = ({ 
  data, 
  selectedYear, 
  selectedMonth, 
  onYearChange, 
  onMonthChange 
}) => {
  if (!data) return <div>Loading...</div>;

  const months = [
    { value: 1, label: 'Januar' }, { value: 2, label: 'Februar' }, { value: 3, label: 'März' },
    { value: 4, label: 'April' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Juni' },
    { value: 7, label: 'Juli' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'Oktober' }, { value: 11, label: 'November' }, { value: 12, label: 'Dezember' }
  ];

  return (
    <div className="space-y-6">
      {/* Date Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Monatliche Auswertung - {data.period.month_name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Jahr</label>
              <Input
                type="number"
                value={selectedYear}
                onChange={(e) => onYearChange(parseInt(e.target.value))}
                min="2020"
                max="2030"
                className="w-24"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Monat</label>
              <select
                value={selectedMonth}
                onChange={(e) => onMonthChange(parseInt(e.target.value))}
                className="p-2 border border-slate-300 rounded-md"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Team Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team-Leistung
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.teams.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">Keine Teams gefunden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium">Team</th>
                    <th className="text-left p-3 font-medium">Typ</th>
                    <th className="text-left p-3 font-medium">Mitglieder</th>
                    <th className="text-left p-3 font-medium">Aktiv</th>
                    <th className="text-left p-3 font-medium">Aufträge</th>
                    <th className="text-left p-3 font-medium">GWV</th>
                    <th className="text-left p-3 font-medium">Connect</th>
                  </tr>
                </thead>
                <tbody>
                  {data.teams.map((team) => (
                    <tr key={team.id} className="border-b hover:bg-slate-50">
                      <td className="p-3 font-medium">{team.name}</td>
                      <td className="p-3">
                        <Badge variant={team.type === 'gwv' ? 'default' : 'secondary'}>
                          {team.type?.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="p-3">{team.member_count}</td>
                      <td className="p-3 text-green-600 font-medium">{team.active_members}</td>
                      <td className="p-3 text-blue-600 font-bold">{team.monthly_assignments_count}</td>
                      <td className="p-3">{team.gwv_assignments}</td>
                      <td className="p-3">{team.connect_assignments}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vacation Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Urlaubsstatistiken
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.vacation_stats && Object.entries(data.vacation_stats).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      status === 'genehmigt' ? 'bg-green-500' :
                      status === 'beantragt' ? 'bg-yellow-500' :
                      status === 'abgelehnt' ? 'bg-red-500' : 'bg-gray-500'
                    }`} />
                    <span className="capitalize">{status}</span>
                  </div>
                  <span className="font-bold">{count}</span>
                </div>
              ))}
              
              {(!data.vacation_stats || Object.keys(data.vacation_stats).length === 0) && (
                <p className="text-slate-500 text-center py-4">Keine Urlaubsanträge für diesen Monat</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Daily Trends Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Tägliche Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.daily_trends.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Keine täglichen Daten verfügbar</p>
            ) : (
              <div className="space-y-2">
                {data.daily_trends.slice(0, 10).map((trend, index) => (
                  <div key={index} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{new Date(trend.date).toLocaleDateString('de-DE')}</span>
                      {trend.type && (
                        <Badge variant={trend.type === 'gwv' ? 'default' : 'secondary'} size="sm">
                          {trend.type.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                    <span className="font-medium">{trend.assignments || trend.count || 0}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Employee Analytics Component
export const EmployeeAnalytics = ({ 
  data, 
  startDate, 
  endDate, 
  onStartDateChange, 
  onEndDateChange 
}) => {
  if (!data) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Mitarbeiter-Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Startdatum</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Enddatum</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => onEndDateChange(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Aktive Mitarbeiter</p>
                <p className="text-2xl font-bold text-blue-600">{data.summary.total_active_employees}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Gesamt Aufträge</p>
                <p className="text-2xl font-bold text-green-600">{data.summary.total_assignments}</p>
              </div>
              <Briefcase className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Urlaubstage</p>
                <p className="text-2xl font-bold text-orange-600">{data.summary.total_vacation_days}</p>
              </div>
              <Calendar className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employee Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle>Mitarbeiter-Leistung (Top Performer)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Nr.</th>
                  <th className="text-left p-3 font-medium">Name</th>
                  <th className="text-left p-3 font-medium">Rolle</th>
                  <th className="text-left p-3 font-medium">Bereich</th>
                  <th className="text-left p-3 font-medium">Aufträge</th>
                  <th className="text-left p-3 font-medium">GWV</th>
                  <th className="text-left p-3 font-medium">Connect</th>
                  <th className="text-left p-3 font-medium">Urlaub</th>
                </tr>
              </thead>
              <tbody>
                {data.employee_performance.slice(0, 15).map((emp) => (
                  <tr key={emp.id} className="border-b hover:bg-slate-50">
                    <td className="p-3 text-sm text-slate-600">{emp.employee_number}</td>
                    <td className="p-3">
                      <div>
                        <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                        <p className={`text-xs ${emp.status === 'aktiv' ? 'text-green-600' : 'text-slate-400'}`}>
                          {emp.status}
                        </p>
                      </div>
                    </td>
                    <td className="p-3 text-sm">{emp.role}</td>
                    <td className="p-3 text-sm">{emp.work_area}</td>
                    <td className="p-3 font-bold text-blue-600">{emp.assignments_count}</td>
                    <td className="p-3">{emp.gwv_assignments}</td>
                    <td className="p-3">{emp.connect_assignments}</td>
                    <td className="p-3">{emp.vacation_days || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Distribution Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Arbeitsbereich-Verteilung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.work_area_distribution.map((area) => (
                <div key={area.area} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="capitalize">{area.area}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ 
                          width: `${(area.count / data.summary.total_active_employees) * 100}%` 
                        }}
                      />
                    </div>
                    <span className="font-bold text-sm">{area.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rollen-Verteilung</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.role_distribution.map((role) => (
                <div key={role.role} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="capitalize">{role.role}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full"
                        style={{ 
                          width: `${(role.count / data.summary.total_active_employees) * 100}%` 
                        }}
                      />
                    </div>
                    <span className="font-bold text-sm">{role.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Vehicle Analytics Component
export const VehicleAnalytics = ({ data }) => {
  if (!data) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Gesamt Fahrzeuge</p>
                <p className="text-2xl font-bold text-blue-600">{data.summary.total_vehicles}</p>
              </div>
              <Car className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Übergaben</p>
                <p className="text-2xl font-bold text-green-600">{data.summary.total_handovers}</p>
              </div>
              <Target className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Wartung fällig</p>
                <p className="text-2xl font-bold text-orange-600">{data.maintenance_alerts.due_soon}</p>
              </div>
              <Clock className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Wartung überfällig</p>
                <p className="text-2xl font-bold text-red-600">{data.maintenance_alerts.overdue}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vehicle Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Car className="w-5 h-5" />
            Fahrzeug-Nutzung (Top 10)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Kennzeichen</th>
                  <th className="text-left p-3 font-medium">Fahrzeug</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Aufträge</th>
                  <th className="text-left p-3 font-medium">Übergaben</th>
                  <th className="text-left p-3 font-medium">Km-Stand</th>
                  <th className="text-left p-3 font-medium">Nächste Wartung</th>
                </tr>
              </thead>
              <tbody>
                {data.vehicle_performance.slice(0, 10).map((vehicle) => (
                  <tr key={vehicle.id} className="border-b hover:bg-slate-50">
                    <td className="p-3 font-bold">{vehicle.license_plate}</td>
                    <td className="p-3">
                      <div>
                        <p className="font-medium">{vehicle.make} {vehicle.model}</p>
                        <p className="text-sm text-slate-500">Baujahr {vehicle.year}</p>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant={
                        vehicle.status === 'verfügbar' ? 'default' :
                        vehicle.status === 'im_einsatz' ? 'secondary' : 'destructive'
                      }>
                        {vehicle.status}
                      </Badge>
                    </td>
                    <td className="p-3 font-bold text-blue-600">{vehicle.assignment_count}</td>
                    <td className="p-3">{vehicle.handover_count}</td>
                    <td className="p-3">{vehicle.mileage?.toLocaleString()} km</td>
                    <td className="p-3">
                      {vehicle.next_maintenance ? 
                        new Date(vehicle.next_maintenance).toLocaleDateString('de-DE') : 
                        'Nicht geplant'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Status Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Status-Verteilung</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.status_distribution.map((status) => (
              <div key={status.status} className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-900">{status.count}</p>
                <p className="text-sm capitalize text-slate-600">{status.status}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Inventory Analytics Component  
export const InventoryAnalytics = ({ data }) => {
  if (!data) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Gesamtwert</p>
                <p className="text-2xl font-bold text-green-600">€{data.summary.total_inventory_value.toLocaleString()}</p>
              </div>
              <Package className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Gesamt Artikel</p>
                <p className="text-2xl font-bold text-blue-600">{data.summary.total_items}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Kategorien</p>
                <p className="text-2xl font-bold text-purple-600">{data.summary.total_categories}</p>
              </div>
              <Target className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Kritische Bestände</p>
                <p className="text-2xl font-bold text-red-600">{data.summary.critical_low_stock}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Kategorien-Übersicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Kategorie</th>
                  <th className="text-left p-3 font-medium">Artikel</th>
                  <th className="text-left p-3 font-medium">Gesamtbestand</th>
                  <th className="text-left p-3 font-medium">Gesamtwert</th>
                  <th className="text-left p-3 font-medium">Niedrig-Bestand</th>
                </tr>
              </thead>
              <tbody>
                {data.category_overview.map((category) => (
                  <tr key={category.category} className="border-b hover:bg-slate-50">
                    <td className="p-3 font-medium capitalize">{category.category}</td>
                    <td className="p-3">{category.total_items}</td>
                    <td className="p-3">{category.total_stock}</td>
                    <td className="p-3 font-bold text-green-600">€{category.total_value.toLocaleString()}</td>
                    <td className="p-3">
                      {category.low_stock_items > 0 ? (
                        <Badge variant="destructive">{category.low_stock_items}</Badge>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top Value Items */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-5 h-5" />
            Wertvollste Artikel (Top 10)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.top_value_items.map((item, index) => (
              <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                    <span className="text-yellow-600 font-bold text-sm">#{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-slate-500 capitalize">{item.category}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">€{item.total_value.toLocaleString()}</p>
                  <p className="text-sm text-slate-500">{item.current_stock} {item.unit}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Low Stock Alerts */}
      {data.low_stock_alerts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Niedrig-Bestand Warnungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.low_stock_alerts.slice(0, 10).map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-slate-600 capitalize">{item.category} • {item.location}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-red-600">{item.current_stock} / {item.min_stock} {item.unit}</p>
                    <p className="text-sm text-red-500">{item.stock_percentage}% vom Mindestbestand</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stock Movements */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Bestandsbewegungen (Letzte 30 Tage)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.stock_movements_30_days.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Keine Bewegungen in den letzten 30 Tagen</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {data.stock_movements_30_days.map((movement) => (
                <div key={movement.type} className="p-4 bg-slate-50 rounded-lg text-center">
                  <div className="flex items-center justify-center mb-2">
                    {movement.type === 'eingang' ? (
                      <ArrowUp className="w-6 h-6 text-green-500" />
                    ) : movement.type === 'ausgang' ? (
                      <ArrowDown className="w-6 h-6 text-red-500" />
                    ) : (
                      <Minus className="w-6 h-6 text-gray-500" />
                    )}
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{movement.total_movements}</p>
                  <p className="text-sm capitalize text-slate-600">{movement.type}</p>
                  <p className="text-xs text-slate-500">Menge: {movement.total_quantity}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Export Center Component
export const ExportCenter = ({ 
  onExport, 
  selectedYear, 
  selectedMonth, 
  startDate, 
  endDate 
}) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Export Center - CSV Downloads
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Monthly Export */}
          <div className="p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Monatliche Team-Analytics</h3>
                <p className="text-sm text-slate-500">
                  Export für {selectedMonth}/{selectedYear} - Teams, Aufträge, Urlaubsstatistiken
                </p>
              </div>
              <Button onClick={() => onExport('monthly')} className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                CSV herunterladen
              </Button>
            </div>
          </div>

          {/* Employee Export */}
          <div className="p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Mitarbeiter-Performance</h3>
                <p className="text-sm text-slate-500">
                  Export für {startDate} bis {endDate} - Mitarbeiterleistung, Aufträge, Urlaubstage
                </p>
              </div>
              <Button onClick={() => onExport('employees')} className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                CSV herunterladen
              </Button>
            </div>
          </div>

          {/* Future Export Options */}
          <div className="p-4 border border-dashed border-slate-300 rounded-lg opacity-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Fahrzeug-Analytics (Zukünftig)</h3>
                <p className="text-sm text-slate-500">
                  Fahrzeugnutzung, Wartungshistorie, Kosten-Analysen
                </p>
              </div>
              <Button disabled className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Bald verfügbar
              </Button>
            </div>
          </div>

          <div className="p-4 border border-dashed border-slate-300 rounded-lg opacity-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Inventar-Berichte (Zukünftig)</h3>
                <p className="text-sm text-slate-500">
                  Bestandswerte, Bewegungshistorie, Wartungsplanung
                </p>
              </div>
              <Button disabled className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Bald verfügbar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Export-Hinweise</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-slate-600">
            <p>• CSV-Dateien können in Excel, Google Sheets oder anderen Tabellenkalkulationsprogrammen geöffnet werden</p>
            <p>• Alle Daten werden in deutscher Formatierung exportiert (Datumsformat: TT.MM.JJJJ)</p>
            <p>• Export-Dateien enthalten nur die Daten für den gewählten Zeitraum</p>
            <p>• Sensitive Daten werden entsprechend der DSGVO-Richtlinien behandelt</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};